import { useState, useMemo, useEffect, useCallback } from 'react'
import { generateVillagers } from '@/economy/villagers'
import { getMarketPrices } from '@/economy/api'
import {
  PROFILE_DISPLAY_NAMES, SPECIALTY_DISPLAY, ITEM_DISPLAY_NAMES,
  VILLAGE_PROFILES, ITEM_BASE_PRICES, formatFollis,
} from '@/economy/types'
import { useEconomyStore } from '@/state/useEconomyStore'
import type { WalletInfo, MarketPriceInfo, ItemType } from '@/economy/types'
import type { InteractionTarget } from '@/state/useEconomyStore'

interface PanelProps { interaction: InteractionTarget; wallet: WalletInfo | null }

interface MarketSnapshot {
  villageIndex: number
  serverId: number
  profile: string
  prices: MarketPriceInfo[]
  fetchedAt: number
}

// Key trade goods to show in the overview (most useful for player arbitrage)
const OVERVIEW_ITEMS: ItemType[] = ['wheat', 'iron', 'wood', 'gold', 'fruit', 'bronze', 'wool', 'weapons']

export function CastlePanel({ interaction }: PanelProps) {
  const [tab, setTab] = useState<'overview' | 'markets' | 'population'>('overview')
  const [globalMarkets, setGlobalMarkets] = useState<MarketSnapshot[]>([])
  const [loadingMarkets, setLoadingMarkets] = useState(false)
  const [selectedItem, setSelectedItem] = useState<ItemType>(OVERVIEW_ITEMS[0])
  const [lastRefresh, setLastRefresh] = useState(0)

  const villageData = useEconomyStore((s) => s.villageData.get(interaction.villageIndex))
  const marketPrices = villageData?.marketPrices ?? null

  const villagers = useMemo(() => {
    if (!interaction.profileType) return []
    const seed = 12345 + interaction.villageIndex * 777
    const sizes: Array<'small' | 'medium' | 'large'> = ['small', 'medium', 'large']
    const size = sizes[interaction.villageIndex % 3]
    const houseCounts = { small: 8, medium: 14, large: 22 }
    return generateVillagers(seed, size, interaction.profileType, houseCounts[size], marketPrices)
  }, [interaction.villageIndex, interaction.profileType, marketPrices])

  const specialtyCounts = new Map<string, number>()
  for (const v of villagers) {
    specialtyCounts.set(v.specialty, (specialtyCounts.get(v.specialty) || 0) + 1)
  }

  const fetchAllMarkets = useCallback(async () => {
    setLoadingMarkets(true)
    const snapshots: MarketSnapshot[] = []
    const now = Date.now()

    // Fetch all 16 markets in parallel
    const fetches = VILLAGE_PROFILES.map(async (vp, idx) => {
      try {
        const prices = await getMarketPrices(vp.serverId)
        return {
          villageIndex: idx,
          serverId: vp.serverId,
          profile: PROFILE_DISPLAY_NAMES[vp.profile],
          prices,
          fetchedAt: now,
        }
      } catch {
        return null
      }
    })

    const results = await Promise.all(fetches)
    for (const r of results) {
      if (r) snapshots.push(r)
    }

    setGlobalMarkets(snapshots)
    setLastRefresh(now)
    setLoadingMarkets(false)
  }, [])

  // Auto-fetch when switching to markets tab
  useEffect(() => {
    if (tab === 'markets' && globalMarkets.length === 0 && !loadingMarkets) {
      fetchAllMarkets()
    }
  }, [tab, globalMarkets.length, loadingMarkets, fetchAllMarkets])

  // Find cheapest/most expensive for selected item across all markets
  const itemAnalysis = useMemo(() => {
    if (globalMarkets.length === 0) return null

    const entries: Array<{ serverId: number; profile: string; price: number; supply: number }> = []
    for (const market of globalMarkets) {
      const p = market.prices.find((pr) => pr.item === selectedItem)
      if (p) {
        entries.push({
          serverId: market.serverId,
          profile: market.profile,
          price: p.spot_price,
          supply: p.pool_item_reserve,
        })
      }
    }

    entries.sort((a, b) => a.price - b.price)
    const basePrice = ITEM_BASE_PRICES[selectedItem]

    return { entries, basePrice }
  }, [globalMarkets, selectedItem])

  return (
    <>
      <div className="panel-tabs">
        <button className={`panel-tab ${tab === 'overview' ? 'panel-tab--active' : ''}`} onClick={() => setTab('overview')}>Overview</button>
        <button className={`panel-tab ${tab === 'markets' ? 'panel-tab--active' : ''}`} onClick={() => setTab('markets')}>Global Markets</button>
        <button className={`panel-tab ${tab === 'population' ? 'panel-tab--active' : ''}`} onClick={() => setTab('population')}>Population</button>
      </div>

      {tab === 'overview' && (
        <>
          <div className="panel-section">
            <h3 className="panel-section__title">Village Overview</h3>
            <div className="panel-row">
              <span className="panel-row__label">Type</span>
              <span className="panel-row__value">{interaction.profileType ? PROFILE_DISPLAY_NAMES[interaction.profileType] : 'Unknown'}</span>
            </div>
            <div className="panel-row">
              <span className="panel-row__label">Population</span>
              <span className="panel-row__value">{villagers.length}</span>
            </div>
            <div className="panel-row">
              <span className="panel-row__label">Defense Level</span>
              <span className="panel-row__value" style={{ color: '#6b8b4a' }}>Guarded</span>
            </div>
          </div>
          <div className="panel-section">
            <h3 className="panel-section__title">Taxes</h3>
            <p style={{ color: '#8b7355', fontFamily: 'Crimson Text, serif', fontSize: 13 }}>
              The lord claims 15% of all production. Trade freely at the market.
            </p>
          </div>
        </>
      )}

      {tab === 'markets' && (
        <>
          <div className="panel-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 className="panel-section__title" style={{ margin: 0 }}>Price Intelligence</h3>
              <button
                className="panel-btn"
                disabled={loadingMarkets}
                onClick={fetchAllMarkets}
                style={{ fontSize: 11 }}
              >
                {loadingMarkets ? 'Fetching...' : 'Refresh'}
              </button>
            </div>
            {lastRefresh > 0 && (
              <p style={{ color: '#8b7355', fontSize: 10, margin: '4px 0 0' }}>
                Last updated {Math.floor((Date.now() - lastRefresh) / 1000)}s ago
              </p>
            )}
          </div>

          {globalMarkets.length === 0 && !loadingMarkets && (
            <p className="panel-placeholder">Press Refresh to fetch market data from all villages.</p>
          )}

          {loadingMarkets && globalMarkets.length === 0 && (
            <p className="panel-placeholder">Fetching prices from {VILLAGE_PROFILES.length} markets...</p>
          )}

          {globalMarkets.length > 0 && (
            <>
              <div className="panel-section">
                <h3 className="panel-section__title">Select Commodity</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {OVERVIEW_ITEMS.map((item) => (
                    <button
                      key={item}
                      className={`panel-btn ${selectedItem === item ? 'panel-btn--primary' : ''}`}
                      onClick={() => setSelectedItem(item)}
                      style={{ fontSize: 11, padding: '3px 8px' }}
                    >
                      {ITEM_DISPLAY_NAMES[item]}
                    </button>
                  ))}
                </div>
              </div>

              {itemAnalysis && (
                <div className="panel-section">
                  <h3 className="panel-section__title">
                    {ITEM_DISPLAY_NAMES[selectedItem]} — All Markets
                    <span style={{ color: '#8b7355', fontSize: 11, fontWeight: 400, marginLeft: 8 }}>
                      (base: {ITEM_BASE_PRICES[selectedItem]}ƒ)
                    </span>
                  </h3>
                  {itemAnalysis.entries.map((e, i) => {
                    const diff = e.price - itemAnalysis.basePrice
                    const pctChange = ((diff / itemAnalysis.basePrice) * 100)
                    const isCheapest = i === 0
                    const isMostExpensive = i === itemAnalysis.entries.length - 1

                    return (
                      <div
                        key={e.serverId}
                        className="panel-row"
                        style={{
                          background: isCheapest ? 'rgba(107, 139, 74, 0.1)' : isMostExpensive ? 'rgba(180, 80, 60, 0.1)' : undefined,
                          borderRadius: 3,
                          padding: '2px 4px',
                        }}
                      >
                        <span className="panel-row__label" style={{ fontSize: 12 }}>
                          <span style={{ color: '#8b7355', fontSize: 10, marginRight: 4 }}>#{e.serverId}</span>
                          {e.profile}
                          {isCheapest && <span style={{ color: '#6b8b4a', fontSize: 10, marginLeft: 4 }}>BUY</span>}
                          {isMostExpensive && itemAnalysis.entries.length > 1 && <span style={{ color: '#b4503c', fontSize: 10, marginLeft: 4 }}>SELL</span>}
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className="panel-row__value">{e.price.toFixed(1)}ƒ</span>
                          <span style={{
                            fontSize: 10,
                            color: pctChange > 0 ? '#b4503c' : pctChange < 0 ? '#6b8b4a' : '#8b7355',
                            minWidth: 40,
                            textAlign: 'right',
                          }}>
                            {pctChange > 0 ? '+' : ''}{pctChange.toFixed(0)}%
                          </span>
                        </span>
                      </div>
                    )
                  })}

                  {itemAnalysis.entries.length >= 2 && (
                    <div style={{ marginTop: 8, padding: '6px 8px', background: 'rgba(196, 185, 154, 0.1)', borderRadius: 4 }}>
                      <p style={{ color: '#c4b99a', fontFamily: 'Crimson Text, serif', fontSize: 12, margin: 0 }}>
                        Arbitrage: buy at #{itemAnalysis.entries[0].serverId} ({formatFollis(itemAnalysis.entries[0].price)}),
                        sell at #{itemAnalysis.entries[itemAnalysis.entries.length - 1].serverId} ({formatFollis(itemAnalysis.entries[itemAnalysis.entries.length - 1].price)})
                        — profit {formatFollis(itemAnalysis.entries[itemAnalysis.entries.length - 1].price - itemAnalysis.entries[0].price)}/unit
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}

      {tab === 'population' && (
        <div className="panel-section">
          <h3 className="panel-section__title">Population Breakdown</h3>
          {Array.from(specialtyCounts.entries()).map(([spec, count]) => (
            <div key={spec} className="panel-row">
              <span className="panel-row__label">{SPECIALTY_DISPLAY[spec as keyof typeof SPECIALTY_DISPLAY]}</span>
              <span className="panel-row__value">{count}</span>
            </div>
          ))}
        </div>
      )}
    </>
  )
}
